import { ts } from '@shared/lib/diagTs';
import * as signalR from '@microsoft/signalr';
import { devAuthUser, env } from '@config/env';
import { ApiClient, DEV_USER_HEADER } from '@core/api/client';
import { lastRestInstanceId, seenRestInstanceIds } from '@core/api/serverInstance';
import { alignHubWithRest, type HubAffinityOutcome } from '@core/realtime/hubAffinity';
import {
  type AutoResumeLatchReason, latchAutoResume, readAutoResumeLatch, releaseAutoResume,
} from '@core/realtime/autoResumeLatch';
import { ResumeBackoff } from '@shared/lib/resumeBackoff';
import { getRelayOwnership, type RelayOwnership } from '@core/realtime/relayOwnership';
import { consumeBootMarker, describeBoot, registerReloadLogSink } from '@core/reloadReason';
import { rti } from '@features/rithmic/proto/rithmic.js';
import { readStoredRithmicCreds } from './rithmicCreds';
import { rc } from './rithmicLog';
import { startRithmicOrderData, onRithmicAccountList, onRithmicFillHistoryResponse, stopRithmicOrderData, RITHMIC_ORDER_TEMPLATE } from './rithmicOrderData';

// ─── FE-built Rithmic login (broker creds are FORBIDDEN from transiting our BE, mirroring CQG) ──────
// The browser holds the credentials (localStorage) and sends the Rithmic `RequestLogin` ITSELF as the
// first frame on every fresh socket. The BE only pushes RithmicOpenSocket(url); everything the logon
// needs beyond the stored creds is a non-secret FE constant (appName/appVersion) or the chosen system.

/** Rithmic protocol version string sent in every RequestLogin (template_version field). Value is the
 *  current TEMPLATE VERSION from the dev-kit change_log (RProtocolAPI 0.89.0.0, template version 5.42,
 *  released 2026-02-06) — the guide (§2.1.a) says to copy it from the proto pool; "5.29" was stale. */
const TEMPLATE_VERSION = '5.42';

// Non-secret app identity for the RequestLogin. **CONFIRMED by Rithmic on 2026-07-29** (conformance
// completion email): "Please use our agreed upon application name of 'OptimusJournal' when connecting
// to any Rithmic system." So this is the registered name for every system and every gateway — do NOT
// vary it per environment, and keep it identical to the BE's `RithmicOptions.AppName`. (The browser
// sends the actual RequestLogin, so THIS constant is the one Rithmic sees.)
const RITHMIC_APP_NAME = 'OptimusJournal';
const RITHMIC_APP_VERSION = '1.0';

// Template ids we branch on locally (every other inbound frame is just relayed to the BE). Read the
// task's Rithmic wire facts: these are the ids carried in the Base.template_id (proto field 154467).
const TEMPLATE = {
  REQUEST_LOGIN: 10,
  RESPONSE_LOGIN: 11,
  REQUEST_LOGOUT: 12,
  RESPONSE_LOGOUT: 13,
  REQUEST_SYSTEM_INFO: 16,
  RESPONSE_SYSTEM_INFO: 17,
  REQUEST_HEARTBEAT: 18,
  RESPONSE_HEARTBEAT: 19,
  REJECT: 75,
  FORCED_LOGOUT: 77,
  EXCHANGE_ORDER_NOTIFICATION: 352,
} as const;

const TEMPLATE_NAMES: Record<number, string> = {
  [TEMPLATE.REQUEST_LOGIN]: 'RequestLogin',
  [TEMPLATE.RESPONSE_LOGIN]: 'ResponseLogin',
  [TEMPLATE.REQUEST_LOGOUT]: 'RequestLogout',
  [TEMPLATE.RESPONSE_LOGOUT]: 'ResponseLogout',
  [TEMPLATE.REQUEST_SYSTEM_INFO]: 'RequestRithmicSystemInfo',
  [TEMPLATE.RESPONSE_SYSTEM_INFO]: 'ResponseRithmicSystemInfo',
  [TEMPLATE.REQUEST_HEARTBEAT]: 'RequestHeartbeat',
  [TEMPLATE.RESPONSE_HEARTBEAT]: 'ResponseHeartbeat',
  [TEMPLATE.REJECT]: 'Reject',
  [TEMPLATE.FORCED_LOGOUT]: 'ForcedLogout',
  [TEMPLATE.EXCHANGE_ORDER_NOTIFICATION]: 'ExchangeOrderNotification',
  // Post-login order-data sequence (see rithmicOrderData.ts) — named for the wire trace.
  [RITHMIC_ORDER_TEMPLATE.REQUEST_ACCOUNT_LIST]: 'RequestAccountList',
  [RITHMIC_ORDER_TEMPLATE.RESPONSE_ACCOUNT_LIST]: 'ResponseAccountList',
  [RITHMIC_ORDER_TEMPLATE.REQUEST_SUBSCRIBE_FOR_ORDER_UPDATES]: 'RequestSubscribeForOrderUpdates',
  [RITHMIC_ORDER_TEMPLATE.RESPONSE_SUBSCRIBE_FOR_ORDER_UPDATES]: 'ResponseSubscribeForOrderUpdates',
  [RITHMIC_ORDER_TEMPLATE.REQUEST_SHOW_ORDERS]: 'RequestShowOrders',
  [RITHMIC_ORDER_TEMPLATE.RESPONSE_SHOW_ORDERS]: 'ResponseShowOrders',
  [RITHMIC_ORDER_TEMPLATE.REQUEST_SHOW_FILL_HISTORY]: 'RequestShowFillHistory',
  [RITHMIC_ORDER_TEMPLATE.RESPONSE_SHOW_FILL_HISTORY]: 'ResponseShowFillHistory',
  // Server-pushed order-plant templates (names per the dev-kit Reference Guide template-id table) —
  // named so the wire trace never reads as an anonymous "templateNNN".
  301: 'LoginInfoResponse',
  305: 'AccountRmsInfoResponse',
  319: 'ResponseShowOrderHistoryDates',
  323: 'ResponseShowOrderHistory',
  350: 'TradeRoute',
  351: 'RithmicOrderNotification',
  353: 'BracketUpdates',
  355: 'UpdateEasyToBorrowList',
  356: 'AccountRmsUpdates',
  357: 'UserInfoUpdate',
};

/** Default heartbeat cadence (seconds) if ResponseLogin.heartbeatInterval is absent/zero. */
const DEFAULT_HEARTBEAT_SECONDS = 60;

/** RithmicOrderNotification: order status, kept in the browser (the BE only Debug-logged it; 2026-09-30). */
const RITHMIC_ORDER_NOTIFICATION_TEMPLATE = 351;

/** A name for one browser socket under relay protocol 2: a UUID where the browser has one. */
function newRelaySocketId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Full decoded-payload console logging for inbound frames (dev/demo only — the "log the REAL data,
 * not something vague" ask, 2026-07-22). Maps an inbound template id to its protobuf decoder; the
 * relay then prints the message's complete decoded JSON to the console (protobufjs `toJSON()`, which
 * omits unset/default fields), so an order notification reads as its actual notify type / status /
 * symbol / price / qty instead of an anonymous byte count.
 *
 * STRICT WHITELIST (security): only these SERVER-pushed, non-credential messages are ever decoded and
 * printed. The credential-bearing templates (RequestLogin 10 / RequestLogout 12) are deliberately
 * absent and must never be added — pinned by `rithmicRelay.decoders.test.ts`. Console-only via `rc`
 * (no-op in prod builds); never routed through `this.log()`, so nothing here reaches the BE.
 */
export const INBOUND_PAYLOAD_DECODERS: Record<number, { decode(body: Uint8Array): { toJSON(): Record<string, unknown> } }> = {
  13: rti.ResponseLogout,
  303: rti.ResponseAccountList,
  309: rti.ResponseSubscribeForOrderUpdates,
  319: rti.ResponseShowOrderHistoryDates,
  321: rti.ResponseShowOrders,
  351: rti.RithmicOrderNotification,   // the order-lifecycle stream (working/modified orders tick here)
  352: rti.ExchangeOrderNotification,  // fills + exchange order lifecycle
  3513: rti.ResponseShowFillHistory,   // historical fills (serial backfill)
};

// ─── Wire framing (Rithmic-specific — the relay is the SOLE owner of the length prefix) ─────────────
// Each Rithmic binary frame on the wire = int32 BIG-ENDIAN (signed) length prefix + protobuf body.
// The BE only ever deals in unframed protobuf BODIES (mirroring CQG, which has no framing at all):
//   - OUTBOUND: the relay frames every body before socket.send (BE-relayed RithmicSend, FE-built
//     logon/heartbeat/sendClient).
//   - INBOUND: the relay strips the 4-byte prefix and relays the raw body to the BE via RithmicInbound.
// buildLoginFrame returns an ALREADY-FRAMED frame (it is sent raw), so the socket only ever receives
// framed bytes; frameBody handles everything else.

/** Prepend the int32 big-endian (signed) length prefix to a protobuf body. */
export function frameBody(body: Uint8Array): Uint8Array {
  const out = new Uint8Array(4 + body.length);
  new DataView(out.buffer).setInt32(0, body.length, false); // big-endian, signed
  out.set(body, 4);
  return out;
}

/** Strip the 4-byte length prefix from an inbound wss frame, yielding the raw protobuf body. */
export function stripFrame(bytes: Uint8Array): Uint8Array {
  return bytes.length >= 4 ? bytes.subarray(4) : bytes;
}

/** Read a frame's declared body length (int32 big-endian). Returns -1 if the frame is too short. */
function readFrameLength(bytes: Uint8Array): number {
  if (bytes.length < 4) return -1;
  return new DataView(bytes.buffer, bytes.byteOffset, 4).getInt32(0, false);
}

/**
 * Build the Rithmic `RequestLogin` frame (template_id 10) from stored creds + the chosen system, ready
 * to send: it is FRAMED (int32-BE length prefix + body). The creds never transit our BE — this frame
 * rides only the browser's Rithmic WebSocket. Exported for tests.
 */
export function buildLoginFrame(
  creds: { username: string; password: string },
  opts: { system: string; appName: string; appVersion: string },
): Uint8Array {
  const body = rti.RequestLogin.encode({
    templateId: TEMPLATE.REQUEST_LOGIN,
    templateVersion: TEMPLATE_VERSION,
    user: creds.username,
    password: creds.password,
    appName: opts.appName,
    appVersion: opts.appVersion,
    systemName: opts.system,
    infraType: rti.RequestLogin.SysInfraType.ORDER_PLANT,
  }).finish();
  return frameBody(body);
}

/**
 * CREDENTIAL guard: true when a protobuf BODY is a `RequestLogin` (template 10) or `RequestLogout`
 * (template 12) — i.e. a frame that carries the plaintext password. Such frames must NEVER be
 * forwarded to the BE (via RithmicInbound). Fails CLOSED: any body we cannot classify returns true
 * (never forward what we can't read). Mirrors CQG's `hasSessionLevelField`. Exported for tests.
 */
export function hasCredentialField(body: Uint8Array): boolean {
  try {
    const templateId = rti.Base.decode(body).templateId;
    if (templateId === TEMPLATE.REQUEST_LOGIN || templateId === TEMPLATE.REQUEST_LOGOUT) return true;
    // Defence-in-depth vs protobuf "last field wins": a crafted body could carry the plaintext
    // password (RequestLogin field 130004) while spoofing Base.template_id to a benign id (e.g. a
    // fill = 352) so the check above passes. The password field NUMBER is unique to RequestLogin —
    // no relayed message reuses it (whereas `user`/131003 IS reused as `user_id` by fills, so we
    // deliberately do NOT key on it). A non-empty decoded password means credentials are physically
    // in the bytes regardless of the advertised template id: never forward it.
    if ((rti.RequestLogin.decode(body).password ?? '').length > 0) return true;
    return false;
  } catch {
    return true; // fail closed — never forward a frame we cannot classify
  }
}

/** Decode a body's Base.template_id (0 if unreadable). Debug-only, never throws. */
function readTemplateId(body: Uint8Array): number {
  try { return rti.Base.decode(body).templateId; } catch { return 0; }
}

/** Human name for an inbound frame's template id (e.g. "ExchangeOrderNotification" or "template312"). */
function describeFrame(body: Uint8Array): string {
  const id = readTemplateId(body);
  return TEMPLATE_NAMES[id] ?? `template${id}`;
}

/**
 * A one-line, human-readable reading of an order/fill frame — "BUY 1 ESZ6 @ 6134.25 · OPEN · acct
 * ZZ99007" instead of "bytes=379" (2026-08-04 user request: "tell me the real data that is coming
 * there, what is the context in human language?").
 *
 * BROWSER CONSOLE ONLY. It is passed as the console override to `log()` and is never relayed to the
 * BE, which receives the same frames and keeps its own decoded `rithmic-wire` log — mirroring the
 * existing rule that decoded payloads stay in this browser.
 *
 * Returns null for anything it cannot summarise faithfully, and the caller falls back to the byte
 * count. A half-guessed sentence about an order is worse than an honest byte count.
 */
function humanPayload(templateId: number, body: Uint8Array): string | null {
  const decoder = INBOUND_PAYLOAD_DECODERS[templateId];
  if (!decoder) return null;
  // 351 RithmicOrderNotification (order lifecycle) and 352 ExchangeOrderNotification (fills + the
  // exchange's own lifecycle) carry the same core shape, so one reader serves both.
  if (templateId !== 351 && templateId !== 352) return null;
  try {
    const m = decoder.decode(body).toJSON() as Record<string, unknown>;
    const str = (k: string) => (typeof m[k] === 'string' && m[k] ? String(m[k]) : null);
    const num = (k: string) => (typeof m[k] === 'number' ? (m[k] as number) : null);

    const side = str('transactionType');                  // BUY / SELL / SS
    const qty = num('fillSize') ?? num('quantity');
    const symbol = [str('symbol'), str('exchange')].filter(Boolean).join('@');
    const price = num('fillPrice') ?? num('price') ?? num('avgFillPrice');
    const parts: string[] = [];

    // The trade itself, in the order a trader would say it out loud.
    const trade = [side, qty, symbol].filter(v => v !== null && v !== '').join(' ');
    if (trade) parts.push(price === null ? trade : `${trade} @ ${price}`);

    // What actually happened to it. `notifyType` is the lifecycle step (FILL / OPEN / CANCELLED…),
    // `status` is Rithmic's own free-text phrasing, and they often disagree in usefulness — so keep
    // whichever are present rather than picking one and hiding the other.
    const state = [str('notifyType'), str('status'), str('completionReason')].filter(Boolean);
    if (state.length > 0) parts.push(state.join(' · '));

    // Identity, so a line can be tied back to an account and an order without opening the object.
    const ids = [
      str('accountId') ? `acct ${str('accountId')}` : null,
      str('basketId') ? `order ${str('basketId')}` : null,
      m.isSnapshot === true ? 'SNAPSHOT' : null,   // a snapshot is history, not a live event
    ].filter(Boolean);
    if (ids.length > 0) parts.push(ids.join(' '));

    return parts.length > 0 ? parts.join(' · ') : null;
  } catch {
    return null;   // unparseable → the caller keeps the byte count, which is at least true
  }
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  return btoa(binary);
}

/**
 * Rithmic conformance **Implementation Check** (email from the Rithmic integration team, 2026-07-21):
 * they ask for the login response's `unique_user_id` plus a **timestamp + timezone** of a typical
 * Rithmic Test session — one per plant/connection — so they can verify from their infra side that the
 * app subscribes for updates + takes a single snapshot and does NOT poll. This prints ONE clearly-marked,
 * copy-pasteable line to the browser console on every SUCCESSFUL login, for the user to copy and send.
 *
 * - **Dev/demo/local only** (`rc` is gated to `MODE !== 'prod'`) and **never relayed to the BE** — it
 *   prints only in this browser's console. `unique_user_id` is an identifier Rithmic itself returns and
 *   asks us to send back; it is not a credential/secret.
 * - **Per plant:** the app opens only the `ORDER_PLANT` connection today, so there is one line per
 *   session. If the PnL / History / Repository plants are ever added (each its own login), each will hit
 *   this same path and print its own line — the `plant` field keeps them distinguishable.
 */
function logRithmicImplementationCheck(login: { uniqueUserId: string; fcmId: string; ibId: string }): void {
  const now = new Date();
  rc.warn(
    '%s [RITHMIC IMPL-CHECK ▶ copy/paste to Rithmic] %o',
    ts(),
    {
      plant: 'ORDER_PLANT',
      infraType: 2, // rti.RequestLogin.SysInfraType.ORDER_PLANT
      uniqueUserId: login.uniqueUserId || '(absent in ResponseLogin)',
      fcmId: login.fcmId || '',
      ibId: login.ibId || '',
      systemName: readStoredRithmicCreds().system,
      loginAtIso: now.toISOString(),
      loginAtLocal: now.toString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // IANA, e.g. "America/Chicago"
      utcOffsetMinutes: -now.getTimezoneOffset(),
    },
  );
}

/** Actual browser→Rithmic WebSocket liveness for this tab. 'open' = a real socket is connected now. */
export type RelayConnState = 'closed' | 'opening' | 'open';

/**
 * Browser-side Rithmic WebSocket relay.
 *
 * A faithful clone of `cqgRelay.ts`: the BE owns all Rithmic protobuf application logic; the browser
 * is an opaque byte pipe to the Rithmic ORDER_PLANT so Rithmic sees the user's IP / Origin rather than
 * the API pod's. Every state transition is forwarded to the BE via `RithmicClientLog` so the backend log
 * shows both sides of the conversation; the per-frame debug lines stay in the browser console (see `log`).
 * The two Rithmic differences from CQG:
 *   1. the wire is length-prefixed (see frameBody / stripFrame);
 *   2. there are no live quotes/positions to decode in the browser — inbound bodies are relayed as-is.
 */
export class RithmicRelay {
  private hub: signalR.HubConnection | null = null;
  private socket: WebSocket | null = null;
  /**
   * The name (a UUID) of `socket` once the BE was told it under relay protocol 2 (RithmicRelayOpenedOn,
   * 2026-09-30), else null. Only a named socket's frames and close carry the name: a socket that opened while
   * the hub was reconnecting, or whose named open failed, was never bound by the BE's session, which would
   * drop its named frames, so it keeps the untagged calls for its whole life.
   */
  private namedSocketId: string | null = null;
  /** The last socket a `RithmicReconnectNeeded` was ignored for, so the ignore is logged once per socket. */
  private ignoredReconnectSocketId: string | null = null;
  private starting: Promise<void> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  // FE-originated outbound frames (via sendClient) queued while the socket isn't open yet; flushed on
  // socket.onopen. Everything else outbound is a BE-built frame relayed via RithmicSend, and every
  // inbound frame except order-status notifications (351, kept in the browser since 2026-09-30) is
  // forwarded to the BE via RithmicInbound / RithmicInboundOn — EXCEPT the login, which the browser
  // builds + sends itself (sendLogonFirst) and never mirrors.
  private outboundQueue: Uint8Array[] = [];

  private connState: RelayConnState = 'closed';
  private connListeners = new Set<() => void>();

  // Listeners for decoded inbound Rithmic frame summaries (e.g. "ResponseLogin",
  // "ExchangeOrderNotification"). The connect/import UI subscribes to advance progress off THIS tab's
  // actual socket rather than racing a separate notifications-hub event.
  private msgListeners = new Set<(summary: string, bytes: number) => void>();

  /** Current browser→Rithmic socket state for this tab. */
  getConnectionState(): RelayConnState { return this.connState; }

  /** Subscribe to socket-state changes (for useSyncExternalStore / React state). Returns unsubscribe. */
  onConnectionChange(listener: () => void): () => void {
    this.connListeners.add(listener);
    return () => { this.connListeners.delete(listener); };
  }

  /** Subscribe to decoded inbound Rithmic frame summaries seen on THIS tab's socket. Returns unsubscribe. */
  onMessage(listener: (summary: string, bytes: number) => void): () => void {
    this.msgListeners.add(listener);
    return () => { this.msgListeners.delete(listener); };
  }

  private emitMessage(summary: string, bytes: number): void {
    this.msgListeners.forEach(l => { try { l(summary, bytes); } catch { /* ignore */ } });
  }

  private setConnState(next: RelayConnState): void {
    if (this.connState === next) return;
    rc.log('%s [rithmic-relay] browser→Rithmic socket: %s → %s', ts(), this.connState, next);
    this.connState = next;
    this.connListeners.forEach(l => { try { l(); } catch { /* ignore */ } });
  }

  private beforeUnloadHandler: (() => void) | null = null;

  /** When this relay last re-established its socket to re-logon (see RELOGON_MIN_INTERVAL_MS). */
  private lastRelogonAt = 0;

  /** Cross-tab ownership (D1, 2026-09-13): a passive tab never opens a socket; an owner yields to a later claim. */
  private readonly ownership: RelayOwnership;
  private pendingYieldRelease = false;
  private reclaimInFlight = false;

  /** The API task the CURRENT hub connection reported (RithmicServerInstance); null until it does. */
  private hubTask: string | null = null;
  /**
   * The relay protocol the CURRENT hub connection's API task speaks (RithmicServerInstance's third
   * argument). From 2 (2026-09-30) frames, opens and closes name the browser socket they came from, so
   * the BE can ignore a closed socket's leftovers. 1 until the task says otherwise (an older task says
   * nothing) and again while the hub reconnects, so no call names a method the task does not have.
   */
  private relayProtocol = 1;
  private hubTaskWaiters: Array<(task: string) => void> = [];
  /** The in-flight hub alignment, shared by concurrent callers (see core/realtime/hubAffinity). */
  private aligning: Promise<HubAffinityOutcome> | null = null;
  /** One REST call whose response names REST's API task (and carries the ALB sticky cookie). */
  private readonly probeRest: () => Promise<unknown>;

  constructor(
    private readonly getAccessToken: () => Promise<string | null>,
    ownership: RelayOwnership = getRelayOwnership('rithmic'),
    probeRest?: () => Promise<unknown>,
  ) {
    this.probeRest = probeRest ?? (() => new ApiClient({ getAccessToken }).get('/api/v1/rithmic/status'));
    this.ownership = ownership;
    this.installOwnershipHandlers();
  }

  /** This tab's cross-tab ownership tracker (badge/dialog read it via useRelayOwnership). */
  getOwnership(): RelayOwnership { return this.ownership; }

  /**
   * Whether a browser-originated frame can go out RIGHT NOW. `sendClient` queues anything handed to it
   * while the socket is down and flushes it on the next socket ahead of the logon ack, so the
   * order-data queue asks this before building a frame from the CURRENT session's routing ids.
   */
  canSendNow(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  /** Forward an app-level line (e.g. `page-reload reason=…`) to the BE client log; console always. */
  clientLog(level: 'info' | 'warn', message: string, detail?: string): void {
    this.log(level, message, detail);
  }

  async start(): Promise<void> {
    rc.log(ts(), '[rithmic-relay] start() entered', { hubState: this.hub?.state ?? 'null', starting: !!this.starting });
    if (this.hub?.state === signalR.HubConnectionState.Connected) {
      // start() runs right before every connect POST (the dialog's Connect and the silent resume both
      // call it first), and that POST creates the session on whichever task REST lands on. A hub that
      // has been up for a while may no longer share that task (the ALB cookie lapsed while the tab was
      // hidden), so re-check and move the hub first; otherwise the session's frames go elsewhere.
      await this.alignHub('pre-connect');
      rc.log(ts(), '[rithmic-relay] start() short-circuit - already connected');
      return;
    }
    if (this.starting) {
      rc.log(ts(), '[rithmic-relay] start() short-circuit - in-flight start, awaiting it');
      return this.starting;
    }

    this.starting = (async () => {
      // A prior hub that is Reconnecting/Disconnected (not Connected — that short-circuits above) must be
      // torn down before we build its replacement; otherwise the old hub's automatic-reconnect loop +
      // handlers leak behind the new one and can double-handle server pushes.
      if (this.hub) {
        try { await this.hub.stop(); } catch { /* ignore */ }
        this.hub = null;
      }

      await this.connectHub();
      // BEFORE anything asks the BE for our session (RithmicResume below, or the caller's connect POST):
      // a hub that connected without a live ALB cookie can sit on a different API task than REST, and
      // then every frame it relays is dropped (dev 2026-09-24). Move it to REST's task first.
      await this.alignHub('hub-start');

      // B3 (2026-09-13): if the previous page reloaded itself, say why — once, from whichever relay
      // hub connects first — so a reload storm is attributable from the BE log alone.
      const boot = consumeBootMarker();
      if (boot) this.log('info', 'rithmic-relay boot', describeBoot(boot));

      // Page refresh / fresh load: this browser has no Rithmic socket yet, but the BE may still hold a
      // Live session whose WS died with the old page. Ask it to resume (re-open WS → re-Login) instead
      // of leaving a "live" badge over a dead socket. (Skipped while latched — a rejected logon, a kick
      // or a user Disconnect must not be silently re-attempted on hub start, even after a reload.)
      this.resumeIfNoSocket('hub-start');

      this.installBeforeUnloadGuard();
    })();

    try {
      await this.starting;
      rc.log(ts(), '[rithmic-relay] start() resolved');
    } catch (err) {
      rc.error(ts(), '[rithmic-relay] start() rejected', err);
      throw err;
    } finally {
      this.starting = null;
    }
  }

  /** Ask the BE to resume our session when this tab has no Rithmic socket (unless latched or passive). */
  private resumeIfNoSocket(trigger: 'hub-start' | 'reconnect'): void {
    if (this.connState === 'open') return;
    const label = trigger === 'hub-start' ? 'rithmic-relay hub-start RithmicResume suppressed' : 'rithmic-relay hub reconnected';
    if (isRithmicAutoResumeBlocked()) {
      this.log('info', label, trigger === 'hub-start'
        ? `auto-resume latched (${describeRithmicLatch()}); awaiting explicit user Connect`
        : `RithmicResume suppressed - auto-resume latched (${describeRithmicLatch()}); awaiting explicit user Connect`);
      return;
    }
    if (this.ownership.isPassive()) {
      this.log('info', label, trigger === 'hub-start'
        ? 'another tab owns the Rithmic connection'
        : 'RithmicResume suppressed - another tab owns the Rithmic connection');
      return;
    }
    const failure = trigger === 'hub-start' ? 'rithmic-relay RithmicResume invoke failed' : 'rithmic-relay RithmicResume (reconnect) failed';
    void this.hub?.invoke('RithmicResume').catch((err) => this.log('warn', failure, String(err)));
  }

  /**
   * Keep the relay hub on the API task REST lands on (core/realtime/hubAffinity). Single-flight: a
   * second caller (e.g. a Connect click while the hub-start alignment runs) shares the first's result.
   */
  private alignHub(reason: string): Promise<HubAffinityOutcome> {
    if (this.aligning) return this.aligning;
    this.aligning = alignHubWithRest('rithmic-relay', {
      probeRest: this.probeRest,
      hubInstance: (timeoutMs) => this.waitHubTask(timeoutMs),
      reconnectHub: () => this.reconnectHub(),
      log: (level, message, detail) => this.log(level, message, detail),
    }, reason).finally(() => { this.aligning = null; });
    return this.aligning;
  }

  /** The task the current hub connection reported, waiting up to `timeoutMs` for its push. */
  private waitHubTask(timeoutMs: number): Promise<string | null> {
    if (this.hubTask) return Promise.resolve(this.hubTask);
    return new Promise((resolve) => {
      const done = (task: string) => { clearTimeout(timer); resolve(task); };
      const timer = setTimeout(() => {
        this.hubTaskWaiters = this.hubTaskWaiters.filter((w) => w !== done);
        resolve(null);
      }, timeoutMs);
      this.hubTaskWaiters.push(done);
    });
  }

  /** Replace ONLY the hub connection. The browser→Rithmic socket is untouched. */
  private async reconnectHub(): Promise<void> {
    const old = this.hub;
    this.hub = null;
    this.hubTask = null;
    if (old) { try { await old.stop(); } catch { /* ignore */ } }
    await this.connectHub();
  }

  /** Build the relay hub, wire its handlers, and connect it (sets `this.hub`). */
  private async connectHub(): Promise<void> {
    const httpOptions: signalR.IHttpConnectionOptions = {};
    if (devAuthUser) {
      httpOptions.transport = signalR.HttpTransportType.LongPolling;
      httpOptions.headers = { [DEV_USER_HEADER]: devAuthUser };
      rc.log(ts(), '[rithmic-relay] start() using LongPolling + dev-auth header');
    } else {
      httpOptions.transport = signalR.HttpTransportType.WebSockets;
      // skipNegotiation: one WS request instead of negotiate+connect — required for correct
      // connects once the API runs >1 task behind the ALB (see hubClient.ts for the full note).
      httpOptions.skipNegotiation = true;
      httpOptions.accessTokenFactory = async () => (await this.getAccessToken()) ?? '';
      rc.log(ts(), '[rithmic-relay] start() using WebSocket (skipNegotiation) + cognito accessTokenFactory');
    }

    const hub = new signalR.HubConnectionBuilder()
      .withUrl(`${env.apiBaseUrl}/hub/rithmic`, httpOptions)
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    hub.on('RithmicOpenSocket', (url: string) => {
      rc.log(ts(), '[rithmic-relay] hub → RithmicOpenSocket', { url });
      this.openSocket(url);
    });
    hub.on('RithmicCloseSocket', () => {
      rc.log(ts(), '[rithmic-relay] hub → RithmicCloseSocket');
      this.closeSocket();
    });
    hub.on('RithmicSend', (payload: ArrayBuffer | Uint8Array | string) => {
      // SignalR's JSON protocol base64-encodes server-sent byte[] into a string on the wire; decode
      // it, then FRAME it (the BE relays unframed protobuf bodies — the relay owns the wire framing).
      const body = this.coerceToBytes(payload);
      rc.log(ts(), '[rithmic-relay] hub → RithmicSend', {
        rawType: typeof payload, isString: typeof payload === 'string', decodedBytes: body.length,
      });
      this.sendToRithmic(body);
    });

    // Which API task holds THIS hub, and does it have our session? Sessions live in per-task memory,
    // so if this task differs from the one that served the connect POST (X-Instance-Id, tracked in
    // core/api/serverInstance.ts) the session is unreachable from here and every frame we relay is
    // dropped — the 2026-07-25 dev failure. Recorded for the hub-affinity check (which moves the hub),
    // and logged so the split stays visible.
    hub.on('RithmicServerInstance', (instanceId: string, hasSession: boolean, relayProtocol?: number) => {
      _hubInstanceId = instanceId ?? null;
      this.hubTask = instanceId ?? null;
      this.relayProtocol = typeof relayProtocol === 'number' ? relayProtocol : 1;
      if (this.hubTask) {
        const waiters = this.hubTaskWaiters;
        this.hubTaskWaiters = [];
        waiters.forEach((w) => w(this.hubTask!));
      }
      const rest = lastRestInstanceId();
      const all = seenRestInstanceIds();
      this.log('info', 'rithmic-relay hub instance',
        `hubTask=${instanceId} hubHasSession=${hasSession} relayProtocol=${this.relayProtocol} lastRestTask=${rest ?? 'n/a'} restTasksSeen=[${all.join(',')}]`);
      if (rest && instanceId && rest !== instanceId) {
        this.log('warn', 'rithmic-relay ⚠ TASK SPLIT',
          `the relay hub is on API task ${instanceId} but REST calls are hitting ${rest} - ` +
          'the Rithmic session is created by the connect POST in per-task memory, so this hub cannot see ' +
          'it and would DROP every relayed frame. The hub-affinity check moves the hub to the REST task.');
      }
    });

    // The BE has no session for us (e.g. the pod rolled mid-session) and is dropping our relayed
    // frames — it asks us to re-establish. Debounced so the per-dropped-frame flood collapses into
    // ONE call; the registered handler (the dialog) silently POSTs a credential-less /rithmic/connect,
    // the BE re-arms + pushes RithmicOpenSocket, and this relay logons itself from stored creds.
    hub.on('RithmicReconnectNeeded', (socketId?: string | null) => this.onReconnectNeededFor(socketId));

    // A reconnect is a NEW WebSocket, routed like a fresh one: until it reports its task, it has none.
    hub.onreconnecting(() => {
      if (this.hub !== hub) return;
      this.hubTask = null;
      this.relayProtocol = 1; // until the reconnected task says which protocol it speaks
    });

    // If SignalR drops and auto-reconnects while this browser's Rithmic socket also died (network
    // blip, laptop sleep), ask the BE to resume so the connection renews itself rather than going dark.
    // First put the reconnected hub back on REST's task (a wake after an hour idle has no live ALB
    // cookie, so the reconnect was routed on its own).
    hub.onreconnected(() => {
      this.log('info', 'rithmic-relay hub reconnected', `wsState=${this.connState}`);
      void this.alignHub('hub-reconnected').then(() => this.resumeIfNoSocket('reconnect'));
    });

    rc.log(ts(), '[rithmic-relay] hub.start() awaiting…');
    this.hubTask = null;
    try {
      await hub.start();
    } catch (err) {
      rc.error(ts(), '[rithmic-relay] hub.start() threw', err);
      throw err;
    }
    this.hub = hub;
    this.log('info', 'rithmic-relay hub connected', `state=${hub.state}`);
  }

  /**
   * `RithmicReconnectNeeded` names the socket whose frame found no session (relay protocol 2, 2026-09-30). A
   * notice about a socket that is not this tab's named socket is ignored, logged once per socket: after a
   * Disconnect, every frame the closed socket still had queued at the BE finds no session, and on dev a
   * notice that arrived just after the user's Connect started a second logon over a socket that was
   * already logged in. A notice naming no socket (an older API task, an untagged frame, a resume) is
   * handled as before.
   */
  private onReconnectNeededFor(socketId: string | null | undefined): void {
    if (typeof socketId === 'string' && socketId.length > 0 && socketId !== this.namedSocketId) {
      if (this.ignoredReconnectSocketId !== socketId) {
        this.ignoredReconnectSocketId = socketId;
        this.log('info', 'rithmic-relay hub → RithmicReconnectNeeded',
          `ignored - about socket ${socketId}, not this tab's current one (${this.namedSocketId ?? 'none'}): a closed socket's leftovers`);
      }
      return;
    }
    this.onReconnectNeeded();
  }

  /** `RithmicReconnectNeeded`: schedule ONE credential-less re-establish on the backoff ladder. */
  private onReconnectNeeded(): void {
    if (isRithmicAutoResumeBlocked()) {
      this.log('info', 'rithmic-relay hub → RithmicReconnectNeeded',
        `suppressed - auto-resume latched (${describeRithmicLatch()}); awaiting explicit user Connect`);
      return;
    }
    if (this.ownership.isPassive()) {
      this.log('info', 'rithmic-relay hub → RithmicReconnectNeeded',
        `suppressed - another tab owns the Rithmic connection (owner=${this.ownership.snapshot().ownerTabId ?? '?'})`);
      return;
    }
    // Backoff ladder (2026-09-13): 5 s → 10 s → 20 s → 40 s → 60 s, then stop and latch until the
    // user Connects. The 3 s throttle it replaces let a session refused on every attempt re-login
    // ~20×/min (prod 2026-09-12, rp_code 13 + close 1011 per attempt).
    const slot = _resumeBackoff.request(() => {
      // Re-check when the attempt FIRES, not only when it was scheduled: a refused login (or a kick,
      // or the user's Disconnect) may have latched auto-resume while it waited. Dev 2026-09-24: an
      // attempt scheduled at 21:08:59 fired at 21:09:05, four seconds after the user's own Connect was
      // refused (rp_code 13), and sent another login Rithmic was certain to refuse.
      if (isRithmicAutoResumeBlocked()) {
        this.log('info', 'rithmic-relay hub → RithmicReconnectNeeded',
          `scheduled attempt dropped - auto-resume latched (${describeRithmicLatch()}) while it waited`);
        return;
      }
      if (this.ownership.isPassive()) {
        this.log('info', 'rithmic-relay hub → RithmicReconnectNeeded',
          'scheduled attempt dropped - another tab took the Rithmic connection while it waited');
        return;
      }
      // Include the task ids: if hubTask !== lastRestTask this is the multi-task split, NOT a rolled
      // pod — re-POSTing connect only helps when it lands on the SAME task that holds this hub.
      this.log('info', 'rithmic-relay hub → RithmicReconnectNeeded',
        `BE has no session; re-establishing via credential-less connect :: hubTask=${_hubInstanceId ?? 'unknown'} ` +
        `lastRestTask=${lastRestInstanceId() ?? 'n/a'} restTasksSeen=[${seenRestInstanceIds().join(',')}]`);
      _reconnectHandler?.();
    });
    if (slot === 'pending') return;
    if (slot === 'exhausted') {
      blockRithmicAutoResume('resume-exhausted');
      this.log('warn', 'rithmic-relay hub → RithmicReconnectNeeded',
        `auto-resume ladder exhausted after ${RITHMIC_RESUME_BACKOFF_MS.length} attempts - latched until the user Connects`);
      return;
    }
    this.log('info', 'rithmic-relay hub → RithmicReconnectNeeded',
      `BE has no session; auto-resume attempt ${slot.attempt}/${RITHMIC_RESUME_BACKOFF_MS.length} in ${slot.delayMs / 1000}s`);
  }

  async stop(): Promise<void> {
    this.log('info', 'rithmic-relay stop requested');
    this.uninstallBeforeUnloadGuard();
    this.stopHeartbeat();
    stopRithmicOrderData();
    this.ownership.release();
    this.setConnState('closed');
    try {
      this.socket?.close();
    } catch {
      /* ignore */
    }
    this.socket = null;
    this.namedSocketId = null;

    if (this.hub) {
      try {
        await this.hub.stop();
      } catch {
        /* ignore */
      }
      this.hub = null;
    }
    this.hubTask = null;
    this.relayProtocol = 1;
  }

  private openSocket(url: string): void {
    // Cross-tab ownership (D1): the BE pushes RithmicOpenSocket to EVERY tab of the user; only the tab
    // that claimed the connection may act on it — a passive tab opening its own socket is exactly the
    // two-context fight that produced rp_code 13 + 1011 ~20×/min in prod (2026-09-12).
    if (this.ownership.isPassive()) {
      this.log('info', 'rithmic-relay openSocket ignored - another tab owns the Rithmic connection',
        `owner=${this.ownership.snapshot().ownerTabId ?? '?'} url=${url}`);
      return;
    }
    // Still CONNECTING: the pending `onopen` will send the RequestLogin, so the asking session WILL get
    // its ack — do nothing. Critically we must NOT tear this down: closing a still-CONNECTING WebSocket
    // kills it with code 1006, and that dead socket's async onclose then nulls out its replacement.
    // (Duplicate RithmicOpenSocket messages arrive on page load when the hub-start resume/RithmicResume
    // races the auto-resume /rithmic/connect — both ask the browser to open a socket.)
    if (this.socket && this.socket.readyState === WebSocket.CONNECTING) {
      this.log('info', 'rithmic-relay openSocket ignored - socket still CONNECTING',
        `url=${url} (its pending logon will ack the asking session)`);
      return;
    }

    // Already OPEN. The BE only pushes RithmicOpenSocket when a session is waiting to be logged on, but
    // OUR logon was already sent + consumed by an EARLIER session — so that waiting session would never
    // get an ack and would sit in `Connecting` forever. This is precisely what stranded connects on
    // 2026-07-25 (the log read "openSocket ignored — already open/opening" and the state never advanced).
    // Re-establish the socket so a fresh RequestLogin + RithmicRelayOpened reach the session that is
    // actually waiting. Re-sending a logon on the LIVE socket is deliberately avoided: Rithmic may
    // Reject a second RequestLogin on an established connection, and a Reject is terminal here.
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      if (isRithmicAutoResumeBlocked()) {
        this.log('info', 'rithmic-relay openSocket while OPEN - suppressed',
          `auto-resume latched (${describeRithmicLatch()}); awaiting explicit user Connect`);
        return;
      }
      const now = Date.now();
      if (now - this.lastRelogonAt < RELOGON_MIN_INTERVAL_MS) {
        // Rate limit: if the asking session keeps being unreachable (e.g. the hub and the connect POST
        // are on different API tasks), reopening on every push would thrash the broker connection.
        this.log('info', 'rithmic-relay openSocket while OPEN - ignored (rate-limited)',
          `url=${url} lastRelogonMsAgo=${now - this.lastRelogonAt}`);
        return;
      }
      this.lastRelogonAt = now;
      this.log('info', 'rithmic-relay openSocket while OPEN - re-establishing to re-logon',
        `url=${url} - a BE session is awaiting a logon ack that our already-consumed logon cannot give it`);
      // Fall through: the reopen below closes this socket and opens a fresh one (logon-first).
    }

    // FE-owned credentials gate: without stored creds the browser can't logon, and an unauthenticated
    // Rithmic socket is useless (Rithmic closes it after the logon timeout) — don't open one. The user
    // must Connect explicitly (which stores creds BEFORE the POST).
    const gateCreds = readStoredRithmicCreds();
    if (!gateCreds.username || !gateCreds.password) {
      this.log('warn', 'rithmic-relay openSocket ignored - no stored Rithmic credentials', 'user must Connect explicitly');
      return;
    }

    this.log('info', 'rithmic-relay opening browser WebSocket', `url=${url}`);
    if (this.socket) {
      // Superseding a lingering CLOSING/CLOSED socket: its onclose early-returns on the identity guard
      // (this.socket !== socket) and so never stops the heartbeat — do it here so a stale heartbeat
      // timer can't outlive the socket it was started for.
      this.stopHeartbeat();
      try { this.socket.close(); } catch { /* ignore */ }
    }

    const socket = new WebSocket(url);
    socket.binaryType = 'arraybuffer';
    const socketId = newRelaySocketId();
    this.socket = socket;
    this.namedSocketId = null; // named at onopen, if the BE is told it
    this.setConnState('opening');

    socket.onopen = () => {
      if (this.socket !== socket) return; // superseded by a newer socket — ignore
      // LOGIN FIRST (the browser owns the Rithmic credentials — the BE never sees them). Rithmic
      // requires the login to be the FIRST frame; it goes out synchronously BEFORE the conn-state
      // flip and BEFORE the outbound-queue flush. Sent DIRECTLY on the socket (already framed).
      this.sendLogonFirst(socket);
      this.setConnState('open');
      this.log('info', 'rithmic-relay browser WebSocket open', `url=${url} socket=${socketId}`);
      this.notifyRelayOpened(socketId);
      // Flush any FE-built frames queued before the socket was ready.
      const pending = this.outboundQueue;
      this.outboundQueue = [];
      for (const body of pending) this.sendToRithmic(body);
    };

    socket.onmessage = (event) => {
      if (this.socket !== socket) return; // stale socket — ignore
      const data = event.data;
      if (data instanceof ArrayBuffer) {
        this.handleInboundFrame(new Uint8Array(data), socketId);
      } else if (data instanceof Blob) {
        // Older Safari may deliver Blob even with binaryType='arraybuffer'; coerce.
        void data.arrayBuffer().then((buf) => {
          if (this.socket === socket) this.handleInboundFrame(new Uint8Array(buf), socketId);
        });
      } else {
        this.log('warn', 'rithmic-relay unexpected text frame from Rithmic; dropping', String(data));
      }
    };

    socket.onerror = (event) => {
      if (this.socket !== socket) return; // stale socket — ignore
      this.log('error', 'rithmic-relay browser WebSocket error', JSON.stringify({
        readyState: socket.readyState,
        type: (event as Event).type,
      }));
    };

    socket.onclose = (event) => {
      if (this.socket !== socket) return; // a superseded socket closing must not touch current state
      this.stopHeartbeat();
      this.setConnState('closed');
      this.log('info', 'rithmic-relay browser WebSocket closed',
        `code=${event.code} reason=${event.reason || '<none>'} wasClean=${event.wasClean}`);
      this.notifyRelayClosed(this.namedSocketId, event.code, event.reason || '');
      this.socket = null;
      this.namedSocketId = null;
      // The order-data catch-up belongs to THIS socket's session; the next accepted login re-arms it.
      stopRithmicOrderData();
      if (this.pendingYieldRelease) this.finishYield();
    };
  }

  /**
   * Handle one inbound wss frame: strip the length prefix, branch locally on the template id, then
   * relay the raw protobuf BODY to the BE (mirroring how CQG relays the decoded body) — EXCEPT
   * credential frames (RequestLogin/RequestLogout), which are never forwarded (fail-closed guard).
   */
  private handleInboundFrame(framed: Uint8Array, socketId: string): void {
    const declaredLen = readFrameLength(framed);
    const body = stripFrame(framed);
    if (declaredLen >= 0 && declaredLen !== body.length)
      this.log('warn', 'rithmic-relay inbound frame length mismatch', `declared=${declaredLen} actual=${body.length}`);

    const templateId = readTemplateId(body);
    const summary = describeFrame(body);
    // The CONSOLE gets the order in plain language (a debug line, so it is never relayed to the BE: the BE
    // decodes the same frame itself, and its `rithmic-wire` log is the server-side trace).
    const human = humanPayload(templateId, body);
    this.log(
      'debug', 'rithmic-relay inbound binary frame', `${summary} bytes=${body.length}`,
      human ? `${summary} - ${human}` : undefined);
    // REAL payload logging (dev/demo console only, whitelist — see INBOUND_PAYLOAD_DECODERS): print the
    // frame's full decoded JSON so the trace shows actual order/fill data, not just a byte count.
    // Deliberately `rc` (never `this.log()`) — payloads stay in the browser console, never relayed to
    // the BE (which has its own decoded `rithmic-wire` log).
    const payloadDecoder = INBOUND_PAYLOAD_DECODERS[templateId];
    if (payloadDecoder) {
      try {
        rc.log('%s [rithmic-relay] ⬇ %s payload %o', ts(), summary, payloadDecoder.decode(body).toJSON());
      } catch {
        rc.warn(ts(), `[rithmic-relay] ⬇ ${summary} payload unparseable (${body.length} bytes)`);
      }
    }
    this.emitMessage(summary, body.length);
    this.handleTemplate(templateId, body);

    // Order-status notifications (351) stay in the browser (2026-09-30): the BE only Debug-logs them, and
    // they were 60% of a logon snapshot (10,400 of 17,300 frames on the owner's Rithmic Test account),
    // each queued at the BE in front of the frames it does need.
    if (templateId === RITHMIC_ORDER_NOTIFICATION_TEMPLATE) return;

    // CREDENTIAL guard (defense in depth — inbound frames are Rithmic responses, never a RequestLogin;
    // a login body here would be a regression). SignalR JSON turns byte[] into base64 on the wire.
    if (hasCredentialField(body)) {
      this.log('warn', 'rithmic-relay inbound credential frame NOT forwarded to BE', `templateId=${templateId}`);
      return;
    }
    const payload = bytesToBase64(body);
    const call = this.relayProtocol >= 2 && this.namedSocketId === socketId
      ? this.hub?.invoke('RithmicInboundOn', socketId, payload)
      : this.hub?.invoke('RithmicInbound', payload);
    void call?.catch((err) => {
      this.log('error', 'rithmic-relay RithmicInbound invoke failed', String(err));
    });
  }

  /**
   * Tells the BE this tab's socket opened. From relay protocol 2 on it names the socket, which its session
   * binds to, and the socket counts as named unless that call fails (then the session never bound to it).
   */
  private notifyRelayOpened(socketId: string): void {
    const hub = this.hub;
    if (!hub) return;
    const named = this.relayProtocol >= 2;
    if (named) this.namedSocketId = socketId;
    const call = named ? hub.invoke('RithmicRelayOpenedOn', socketId) : hub.invoke('RithmicRelayOpened');
    void call.catch((err) => {
      if (this.namedSocketId === socketId) this.namedSocketId = null;
      this.log('error', 'rithmic-relay RithmicRelayOpened invoke failed', String(err));
    });
  }

  /** Tells the BE this tab's socket closed, naming it if it is named (the BE ignores another socket's close). */
  private notifyRelayClosed(namedSocketId: string | null, code: number, reason: string): void {
    const call = this.relayProtocol >= 2 && namedSocketId
      ? this.hub?.invoke('RithmicRelayClosedOn', namedSocketId, code, reason)
      : this.hub?.invoke('RithmicRelayClosed', code, reason);
    void call?.catch(() => {});
  }

  /** Local branching on inbound template ids (heartbeat lifecycle + terminal-rejection latch). */
  private handleTemplate(templateId: number, body: Uint8Array): void {
    switch (templateId) {
      case TEMPLATE.RESPONSE_LOGIN: {
        // A login RESPONSE is NOT proof of success: a REJECTED login also arrives as ResponseLogin (11)
        // carrying rp_code — rp_code[0]=="0" ⇒ accepted, anything else ⇒ refused (user_msg has the text).
        // Treat a non-"0" rp_code as terminal (like a Reject) so we never start a heartbeat on a session
        // Rithmic refused, nor leave a "live" badge over a dead logon.
        let rpCode: string[] = [];
        let userMsg: string[] = [];
        let seconds = DEFAULT_HEARTBEAT_SECONDS;
        let uniqueUserId = '';
        let fcmId = '';
        let ibId = '';
        try {
          const resp = rti.ResponseLogin.decode(body);
          rpCode = resp.rpCode ?? [];
          userMsg = resp.userMsg ?? [];
          uniqueUserId = resp.uniqueUserId ?? '';
          fcmId = resp.fcmId ?? '';
          ibId = resp.ibId ?? '';
          if (typeof resp.heartbeatInterval === 'number' && resp.heartbeatInterval > 0) seconds = resp.heartbeatInterval;
        } catch { /* keep defaults; an unparseable ResponseLogin is treated as accepted (BE decides state) */ }
        const accepted = rpCode.length === 0 || rpCode[0] === '0';
        if (!accepted) {
          // The FE half of the Rithmic-report line (2026-09-13): what Rithmic answered plus exactly
          // what THIS browser sent in the RequestLogin (the BE never sees that frame). Never the
          // username/password. The BE half is `rithmic-logon-rejected` (prod-issues query C4).
          this.log('warn', 'rithmic-relay ⬇ ResponseLogin REJECTED',
            `rpCode=${rpCode.join(',') || '<none>'} userMsg=${userMsg.join(' ') || '<none>'} ` +
            `templateVersion=${TEMPLATE_VERSION} app=${RITHMIC_APP_NAME}/${RITHMIC_APP_VERSION} infra=ORDER_PLANT ` +
            `system=${readStoredRithmicCreds().system || '<none>'} gateway=${this.socket?.url ?? '<none>'} ` +
            `fcmId=${fcmId || '<none>'} ibId=${ibId || '<none>'}`);
          blockRithmicAutoResume('logon-rejected');
          this.stopHeartbeat();
          this.reclaimInFlight = false;
          this.ownership.failed();
          break;
        }
        this.log('info', 'rithmic-relay ⬇ ResponseLogin - logged in', `heartbeat=${seconds}s`);
        clearRithmicLatchAfterAcceptedLogon();
        this.startHeartbeat(seconds);
        this.reclaimInFlight = false;
        this.ownership.connected();
        // Rithmic conformance Implementation Check: emit the copy-paste line for this connection's login.
        logRithmicImplementationCheck({ uniqueUserId, fcmId, ibId });
        // Kick off the FE-driven order-data sequence: account list → per-account subscribe + single
        // show_orders snapshot (Rithmic's "subscribe + snapshot, don't poll" pattern). Fills then stream
        // as ExchangeOrderNotification (352), already ingested by the BE.
        startRithmicOrderData(this, { fcmId, ibId });
        break;
      }
      case TEMPLATE.REJECT: {
        // Terminal — latch off all auto-resume so we don't hammer Rithmic with the same rejected creds
        // on every SignalR blip (cleared only by an explicit user Connect). Log the DECODED reason so a
        // rejection is diagnosable from the console/BE log rather than a bare "Reject".
        let detail = 'Reject';
        try {
          const r = rti.Reject.decode(body);
          detail = `Reject rpCode=${(r.rpCode ?? []).join(',') || '<none>'} userMsg=${(r.userMsg ?? []).join(' ') || '<none>'}`;
        } catch { /* keep the generic label */ }
        this.log('warn', 'rithmic-relay ⬇ terminal frame', detail);
        blockRithmicAutoResume('logon-rejected');
        this.stopHeartbeat();
        this.reclaimInFlight = false;
        this.ownership.failed();
        break;
      }
      case TEMPLATE.FORCED_LOGOUT:
        // ForcedLogout (77) carries no reason payload in the proto — Rithmic ended the session and said
        // no more. Latch DURABLY as a kick (D4, 2026-09-13): no tab, and no reload, auto-resumes into it.
        this.log('warn', 'rithmic-relay ⬇ terminal frame', 'ForcedLogout (Rithmic ended the session; proto carries no reason)');
        blockRithmicAutoResume('kicked');
        this.stopHeartbeat();
        this.ownership.release();
        break;
      case TEMPLATE.RESPONSE_HEARTBEAT:
        // Liveness only — no action needed.
        break;
      case RITHMIC_ORDER_TEMPLATE.RESPONSE_ACCOUNT_LIST:
        // FE order-data driver: per account_id → subscribe for order updates + one show_orders snapshot.
        // The raw frame is STILL forwarded to the BE below (which registers the account) — this is purely
        // additive local handling, never a substitute for the BE forward.
        onRithmicAccountList(this, body);
        break;
      case RITHMIC_ORDER_TEMPLATE.RESPONSE_SUBSCRIBE_FOR_ORDER_UPDATES:
        // Subscription ack — order updates (fills as ExchangeOrderNotification 352) will now stream.
        this.log('info', 'rithmic-relay ⬇ ResponseSubscribeForOrderUpdates - order updates subscribed');
        break;
      case RITHMIC_ORDER_TEMPLATE.RESPONSE_SHOW_FILL_HISTORY:
        // Historical fill (or the terminal completion). The raw frame is STILL forwarded to the BE below
        // (which ingests each historical fill); this local hook only advances the SERIAL window queue
        // when the terminal (rp_code-bearing) message arrives.
        onRithmicFillHistoryResponse(body);
        break;
      default:
        break;
    }
  }

  private closeSocket(): void {
    this.log('info', 'rithmic-relay close requested by server');
    this.stopHeartbeat();
    this.setConnState('closed');
    if (this.socket) {
      try {
        this.socket.close(1000, 'server requested close');
      } catch {
        /* ignore */
      }
      this.socket = null;
      this.namedSocketId = null;
    }
  }

  /**
   * Send a browser-built Rithmic frame (an unframed protobuf body — this framing helper prepends the
   * length prefix). If the socket isn't open yet, queue it and flush on open. FE-originated only; it is
   * never routed to the BE. All other traffic is unchanged: BE-built frames relay via RithmicSend, and
   * every inbound frame goes to the BE via RithmicInbound.
   */
  sendClient(body: Uint8Array): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.sendToRithmic(body);
    } else {
      this.outboundQueue.push(body);
    }
  }

  /**
   * Build + send the Rithmic `RequestLogin` as the FIRST frame on a freshly-opened socket (the browser
   * owns the credentials; they never transit our BE). Reads creds from localStorage (guaranteed present
   * — openSocket gates on them) and the chosen system. Sent with socket.send directly (already framed):
   * this bypasses sendToRithmic's outbound logging + framing. The BE log only learns non-secret facts.
   */
  private sendLogonFirst(socket: WebSocket): void {
    const creds = readStoredRithmicCreds();
    if (!creds.username || !creds.password) {
      // Unreachable via openSocket (it gates on creds), but a cleared-storage race must not throw.
      this.log('warn', 'rithmic-relay logon skipped - no stored credentials', 'user must Connect explicitly');
      return;
    }
    const frame = buildLoginFrame(
      { username: creds.username, password: creds.password },
      { system: creds.system, appName: RITHMIC_APP_NAME, appVersion: RITHMIC_APP_VERSION },
    );
    socket.send(frame);
    // BE-forwarded line — REDACTED (no user/password): this.log() also relays to the backend via
    // RithmicClientLog, and broker credentials must never transit our BE.
    this.log('info', 'rithmic-relay ⬆ RequestLogin sent (FE-built)',
      `system=${creds.system} appName=${RITHMIC_APP_NAME} bytes=${frame.length}`);
    // DEV/DEMO BROWSER-CONSOLE ONLY: the FULL RequestLogin contents, INCLUDING the plaintext password,
    // so you can verify exactly what was sent to Rithmic while debugging. This uses rc (gated to
    // MODE !== 'prod') and is NEVER relayed to the BE — it prints only in this browser's console, and
    // is a no-op in production. Do not route this through this.log().
    rc.warn('[rithmic-relay] ⬆ RequestLogin FULL - dev/demo console only, INCLUDES PASSWORD', {
      gatewayUrl: socket.url,
      templateId: TEMPLATE.REQUEST_LOGIN,
      templateVersion: TEMPLATE_VERSION,
      user: creds.username,
      password: creds.password,
      appName: RITHMIC_APP_NAME,
      appVersion: RITHMIC_APP_VERSION,
      systemName: creds.system,
      infraType: 'ORDER_PLANT (2)',
      frameBytes: frame.length,
    });
  }

  /** Frame a protobuf body (int32-BE length prefix) and send it over the Rithmic socket. */
  private sendToRithmic(body: Uint8Array): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      this.log('warn', 'rithmic-relay outbound dropped - browser WS not open',
        `type=${describeFrame(body)} wsState=${this.socket?.readyState ?? 'null'} bytes=${body.length}`);
      return;
    }
    this.log('debug', 'rithmic-relay outbound binary frame', `${describeFrame(body)} bytes=${body.length}`);
    this.socket.send(frameBody(body));
  }

  /** Start the heartbeat interval (RequestHeartbeat template 18, framed). Idempotent — resets the timer. */
  private startHeartbeat(seconds: number): void {
    this.stopHeartbeat();
    // An accepted logon that stays up for a minute lets the auto-resume ladder start over from 5 s.
    _resumeBackoff.markHealthy();
    const ms = Math.max(1, seconds) * 1000;
    this.heartbeatTimer = setInterval(() => {
      const body = rti.RequestHeartbeat.encode({ templateId: TEMPLATE.REQUEST_HEARTBEAT }).finish();
      // Route through sendToRithmic (not a raw socket.send) so each heartbeat is visible at debug and a
      // missed-heartbeat disconnect is diagnosable — it also owns the socket-open guard + framing.
      this.sendToRithmic(body);
    }, ms);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) { clearInterval(this.heartbeatTimer); this.heartbeatTimer = null; }
    // Every terminal path (close, rejection, kick) lands here: a session that did not stay up for the
    // stable window must not reset the auto-resume ladder.
    _resumeBackoff.markUnhealthy();
  }

  /**
   * SignalR's JSON protocol turns server-sent byte[] into base64 strings on the wire. The hub methods
   * on @microsoft/signalr deliver the raw deserialized value (a string). Accept whichever shape arrives
   * and yield a Uint8Array.
   */
  private coerceToBytes(payload: ArrayBuffer | Uint8Array | string): Uint8Array {
    if (typeof payload === 'string') {
      const binary = atob(payload);
      const out = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
      return out;
    }
    if (payload instanceof Uint8Array) return payload;
    return new Uint8Array(payload);
  }


  /**
   * Cross-tab ownership hooks (D1, 2026-09-13 — "the last tab to ask is the one that gets connected").
   * `yield`: another tab claimed the Rithmic connection — close ours cleanly (1000), let the BE hear
   * `RithmicRelayClosed` from the socket's onclose, THEN `release()` so the claimant's `/connect` lands on a
   * session that already saw our socket go. `reclaim`: the tab that displaced us failed its logon —
   * one silent reconnect from here, never a loop. `owner-gone`: the owner released (Disconnect / tab
   * close) — a passive tab with stored creds resumes once. Both resumes honour the D4 latch.
   */
  private installOwnershipHandlers(): void {
    this.ownership.onEvent((event) => {
      if (event.type === 'yield') {
        this.log('info', 'rithmic-relay yielding the Rithmic connection to another tab', `owner=${event.toTabId}`);
        this.stopHeartbeat();
        if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
          this.pendingYieldRelease = true;
          try { this.socket.close(1000, `yielded to tab ${event.toTabId}`); } catch { this.finishYield(); }
        } else {
          this.finishYield();
        }
        return;
      }
      if (event.type === 'reclaim' || event.type === 'owner-gone') {
        const from = event.type === 'reclaim' ? event.fromTabId : event.tabId;
        // A reclaim may run past a `logon-rejected` latch: that latch was just written by the tab whose
        // logon failed, and the creds are shared browser-wide — the displaced owner gets ONE attempt
        // ("the last tab wins if its connection succeeds"). A kick, a user Disconnect or an exhausted
        // ladder still stand.
        const latch = readAutoResumeLatch('rithmic');
        const blocked = event.type === 'reclaim'
          ? (latch !== null && latch !== 'logon-rejected')
          : isRithmicAutoResumeBlocked();
        if (blocked) {
          this.log('info', `rithmic-relay ${event.type} - not resuming`, `auto-resume latched (${latch ?? 'this-tab'}); tab=${from}`);
          return;
        }
        if (this.reclaimInFlight) return;
        this.reclaimInFlight = true;
        this.log('info', `rithmic-relay ${event.type} - resuming from this tab once`, `tab=${from}`);
        _reconnectHandler?.();
      }
    });
  }

  /** The yield handshake's last step: our socket is gone, tell the other tabs (and the claimant). */
  private finishYield(): void {
    this.pendingYieldRelease = false;
    this.ownership.release();
  }

  /**
   * Tab-close cleanup. The browser owns the WS to Rithmic; if the tab dies without explicitly calling
   * stop(), the WS hangs around until Rithmic reaps it on idle. A `beforeunload` listener synchronously
   * closes the browser WS and fires a best-effort hub invocation so the BE transitions to Disconnected.
   */
  private installBeforeUnloadGuard(): void {
    if (typeof window === 'undefined') return;
    if (this.beforeUnloadHandler) return;
    const handler = () => {
      // Only a tab that actually holds the broker socket reports the close: the BE session is per USER,
      // so a passive tab's RelayClosed would flip the OWNER's live session to Disconnected and gate its
      // sends (dev 2026-09-13: a passive tab's sign-out suppressed the owner's next heartbeat).
      const hadSocket = this.socket !== null;
      const closingSocketId = this.namedSocketId;
      this.stopHeartbeat();
      this.setConnState('closed');
      try { this.socket?.close(1001, 'page unload'); } catch { /* ignore */ }
      this.socket = null;
      this.namedSocketId = null;
      if (hadSocket) {
        try { this.notifyRelayClosed(closingSocketId, 1001, 'page unload'); } catch { /* ignore */ }
      }
      this.ownership.release();
    };
    window.addEventListener('beforeunload', handler);
    this.beforeUnloadHandler = handler;
  }

  private uninstallBeforeUnloadGuard(): void {
    if (typeof window === 'undefined') return;
    if (!this.beforeUnloadHandler) return;
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    this.beforeUnloadHandler = null;
  }

  /**
   * Forward a log line to the BE (and the browser console). Best-effort: if the hub isn't connected
   * yet, the BE invoke is silently dropped — the console.log still happens, so nothing is truly lost.
   * Debug lines are console-only (2026-09-30).
   */
  /**
   * `consoleDetail` (2026-08-04) replaces `detail` in the BROWSER line only — the BE still receives
   * `detail`. It exists so the inbound-frame trace can read as decoded order data locally while the
   * relayed log stays a byte count, keeping decoded payloads in the browser as the payload logging
   * below already does.
   */
  private log(
    level: 'info' | 'warn' | 'error' | 'debug', message: string, detail?: string,
    consoleDetail?: string,
  ): void {
    const shown = consoleDetail ?? detail;
    const consoleLine = shown ? `${message} :: ${shown}` : message;
    if (level === 'error') rc.error(ts(), '[rithmic-relay]', consoleLine);
    else if (level === 'warn') rc.warn(ts(), '[rithmic-relay]', consoleLine);
    else if (level === 'debug') rc.debug(ts(), '[rithmic-relay]', consoleLine);
    else rc.log(ts(), '[rithmic-relay]', consoleLine);

    // Debug lines (one per relayed frame, in and out) stay in the browser console. The BE logs a forwarded
    // debug line at Debug, a level no environment keeps, so each one cost a hub call and a biz-op line for
    // nothing: prod 2026-09-29 made 187,379 of those calls, as many as the Rithmic frames themselves.
    if (level === 'debug') return;
    const hub = this.hub;
    if (hub && hub.state === signalR.HubConnectionState.Connected) {
      void hub.invoke('RithmicClientLog', level, message, detail ?? null).catch(() => {});
    }
  }
}

// ─── App-level singleton ─────────────────────────────────────────────────────
// One relay for the whole app (mirrors the CQG singleton): the connect dialog + state badge share ONE
// session so a page load opens exactly one hub + one Rithmic socket. The access-token getter is
// injected via a module ref so the singleton always uses the latest auth without being re-created.
let _singleton: RithmicRelay | null = null;
// B3: `page-reload reason=…` lines ride this relay's client log (→ BE `[rithmic-fe]`) when it exists.
registerReloadLogSink((level, message, detail) => _singleton?.clientLog(level, message, detail));
let _tokenGetter: () => Promise<string | null> = async () => null;

/** Keep the singleton's access-token source current (called from the dialog on every render). */
export function setRithmicTokenGetter(fn: () => Promise<string | null>): void {
  _tokenGetter = fn;
}

let _reconnectHandler: (() => void) | null = null;
/**
 * Auto-resume backoff ladder for `RithmicReconnectNeeded` (2026-09-13 plan, task B2): attempts wait
 * 5 s, 10 s, 20 s, 40 s, 60 s, then stop (latched `resume-exhausted` until an explicit Connect). It
 * resets after a logon has stayed up for a minute, or on an explicit Connect. Exported so tests and
 * the docs pin the schedule.
 */
export const RITHMIC_RESUME_BACKOFF_MS: readonly number[] = [5_000, 10_000, 20_000, 40_000, 60_000];
const RITHMIC_RESUME_STABLE_AFTER_MS = 60_000;
const _resumeBackoff = new ResumeBackoff(RITHMIC_RESUME_BACKOFF_MS, RITHMIC_RESUME_STABLE_AFTER_MS);
/** API task id holding the relay hub (from the RithmicServerInstance push) — diagnostics only. */
let _hubInstanceId: string | null = null;

/**
 * Floor between socket re-establishes triggered by a `RithmicOpenSocket` arriving while the socket is
 * already OPEN (see `openSocket`). One reopen recovers a session that missed its logon ack; repeating
 * it every push would thrash the broker connection when the asking session is permanently unreachable
 * (e.g. hub and connect POST on different API tasks). The timestamp itself is per-relay INSTANCE state
 * (it is about this relay's socket), unlike the module-level auto-resume latches above.
 */
const RELOGON_MIN_INTERVAL_MS = 10_000;

/** The API task holding the relay hub, or null before the hub reports it. */
export function rithmicHubInstanceId(): string | null {
  return _hubInstanceId;
}

/**
 * Register what to do when the BE signals `RithmicReconnectNeeded` (it has no session for us and is
 * dropping our frames). The dialog sets this to a SILENT credential-less POST /rithmic/connect — the
 * BE re-arms + pushes RithmicOpenSocket and the relay logons itself from stored creds at socket-open.
 * The hub handler debounces the per-frame flood into a single call.
 */
export function setRithmicReconnectHandler(fn: () => void): void {
  _reconnectHandler = fn;
}

// ── Terminal latch: rejection / kick / user Disconnect ──────────────────────────────────────────
// A Reject / refused ResponseLogin / ForcedLogout is TERMINAL (the BE pushes rithmic.state='error'),
// and so is the user's own Disconnect. Without this latch the auto-resume paths (RithmicReconnectNeeded,
// hub-onreconnected→RithmicResume, hub-start RithmicResume, re-logon on an open socket) would re-send
// the SAME creds on every SignalR blip. When latched, all auto-resume is suppressed until the user
// EXPLICITLY re-Connects (allowRithmicAutoResume). Since 2026-09-13 (D4) the latch is also DURABLE —
// `@core/realtime/autoResumeLatch` in localStorage, shared by every tab and kept across reloads — because
// the in-memory latch alone died with each reload and prod re-logged-on into the same rp_code 13 ~20×/min.
let _autoResumeBlocked = false;

/** Latch auto-resume off (this tab now, every tab + reload via the durable latch) with the reason. */
export function blockRithmicAutoResume(reason: AutoResumeLatchReason = 'logon-rejected'): void {
  _autoResumeBlocked = true;
  latchAutoResume('rithmic', reason);
}

/** Clear both latches and the backoff ladder — called on an explicit user Connect (a deliberate retry). */
export function allowRithmicAutoResume(): void {
  _autoResumeBlocked = false;
  releaseAutoResume('rithmic');
  _resumeBackoff.reset();
}

/** Whether auto-resume is currently suppressed — by this tab's latch or the durable one. */
export function isRithmicAutoResumeBlocked(): boolean {
  return _autoResumeBlocked || readAutoResumeLatch('rithmic') !== null;
}

/** The durable latch reason for log lines ("this-tab" = only the in-memory latch is set). */
function describeRithmicLatch(): string {
  return readAutoResumeLatch('rithmic') ?? 'this-tab';
}

/**
 * A logon Rithmic ACCEPTED ends the reason for the latch (2026-09-14): a kick, an earlier rejection or
 * a Disconnect the user has since undone are all settled by a working session, so the next page load
 * may auto-resume again. Deliberately does NOT reset the backoff ladder — the ladder exists because
 * the BE keeps losing our session (`RithmicReconnectNeeded`), which an accepted BROKER logon says
 * nothing about; it keeps its own "healthy for a minute" rule (dev 2026-09-14: a task-split loop
 * re-logged-on successfully every ~30 s while the BE still had no session for us).
 */
function clearRithmicLatchAfterAcceptedLogon(): void {
  if (!_autoResumeBlocked && readAutoResumeLatch('rithmic') === null) return;
  _autoResumeBlocked = false;
  releaseAutoResume('rithmic');
}

/** Test seam: the module-level backoff ladder. */
export function _rithmicResumeBackoffForTests(): ResumeBackoff {
  return _resumeBackoff;
}

/** The shared app-level Rithmic relay (lazily created). */
export function getRithmicRelay(): RithmicRelay {
  _singleton ??= new RithmicRelay(() => _tokenGetter());
  return _singleton;
}

/**
 * Abort an in-progress (not-yet-live) Rithmic connect/resume — the "Cancel" on the connecting dialog.
 * Tells the BE to drop the session (which pushes `RithmicCloseSocket` back, closing the browser socket)
 * AND stops this tab's relay, so nothing keeps re-opening the socket. Best-effort: BOTH run even if one
 * fails, so Cancel always cancels. (A LIVE session is torn down via the Disconnect button, not this.)
 */
export async function abortRithmicConnection(opts: {
  disconnect: () => Promise<unknown>;
  stopRelay: () => Promise<void>;
}): Promise<void> {
  await Promise.allSettled([
    Promise.resolve().then(opts.disconnect),
    Promise.resolve().then(opts.stopRelay),
  ]);
}
